import React, { useState } from "react"

import TextButton from "@components/elements/V2/Buttons/TextButton"
import GatsbyStoryblokImage from "@components/elements/V2/GatsbyStoryblokImage"
import CircleCheckIcon from "@components/elements/V2/Icons/CircleCheckIcon"
import Typography from "@components/elements/V2/Typography"
import { Color } from "@constants/V2/color"

type Props = {
  boxContent: {
    title: string
    imageUrl?: string
    imageAlt?: string
  }[]
}

const DEFAULT_VISIBLE_ROWS = 5

const BoxContent = ({ boxContent }: Props) => {
  const [isExpanded, setIsExpanded] = useState(false)

  const hasMoreThanDefault = boxContent.length > DEFAULT_VISIBLE_ROWS
  const visibleContent = isExpanded
    ? boxContent
    : boxContent.slice(0, DEFAULT_VISIBLE_ROWS)

  return (
    <div className="flex flex-col gap-16">
      <Typography
        text="What's in the box"
        font="grotesk"
        color={Color.Charcoal}
        size="body-lg"
        weight="medium"
      />
      <div className="relative">
        <div className="flex flex-col">
          {visibleContent.map((item, index) => (
            <React.Fragment key={`${item.title}-${index}`}>
              {index > 0 && (
                <hr className="ml-[66px] border-charcoal opacity-10" />
              )}
              <div className="flex items-center gap-20 py-4 pl-10">
                <div className="size-[36px] shrink-0">
                  {item.imageUrl && (
                    <GatsbyStoryblokImage
                      image={item.imageUrl}
                      alt={item.imageAlt ?? ""}
                      aspectRatio="1:1"
                      width={36}
                      height={36}
                    />
                  )}
                </div>
                <Typography
                  text={item.title}
                  font="grotesk"
                  color={Color.Charcoal}
                  size="body-sm"
                  weight="book"
                  className="flex-1"
                />
                <CircleCheckIcon
                  width={16}
                  height={16}
                  checkmarkClassName="stroke-charcoal"
                  borderClassName="stroke-charcoal"
                />
              </div>
            </React.Fragment>
          ))}
        </div>
        {!isExpanded && hasMoreThanDefault && (
          <div className="pointer-events-none absolute inset-x-0 bottom-0 h-80 bg-gradient-to-t from-white to-transparent" />
        )}
      </div>
      {hasMoreThanDefault && (
        <TextButton
          text={isExpanded ? "Read less" : "Read more"}
          style="underlined"
          color={Color.Blue}
          onClick={() => setIsExpanded(!isExpanded)}
          textSize="body-sm"
        />
      )}
    </div>
  )
}

export default BoxContent
